import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import FormField from '../../components/ui/FormField.jsx';
import Modal from '../../components/ui/Modal.jsx';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import { confirmDelete, toastError, toastSuccess } from '../../hooks/useToast.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useUpload } from '../../hooks/useUpload.js';
import {
  ADMIN_DELETE_BTN_CLASS,
  ADMIN_ICON_BTN_CLASS,
  ADMIN_INPUT_CLASS,
  adminPublishStatusClasses,
} from '../../constants/adminUi.js';

const inputClass = ADMIN_INPUT_CLASS;

const empty = {
  titulo: '',
  imagen: '',
  enlace: '',
  estado: false,
  orden: '',
};

export default function HomeEventosPopupAdminPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);

  const { uploadFile, uploading } = useUpload();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: empty });

  const imagenPath = watch('imagen');

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/v1/home-popup', {
        params: { page, per_page: perPage, search: debouncedSearch },
      });
      if (data.success) {
        setItems(data.data.items);
        setTotal(data.data.total);
      }
    } catch (e) {
      toastError('Error', e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch]);

  function openCreate() {
    setEditingId(null);
    reset(empty);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditingId(row.id);
    reset({
      titulo: row.titulo ?? '',
      imagen: row.imagen ?? '',
      enlace: row.enlace ?? '',
      estado: Number(row.estado) === 1,
      orden: row.orden != null && row.orden !== '' ? String(row.orden) : '',
    });
    setModalOpen(true);
  }

  async function onSubmit(values) {
    const payload = {
      titulo: values.titulo.trim(),
      imagen: values.imagen.trim(),
      enlace: values.enlace?.trim() ?? '',
      estado: values.estado ? 1 : 0,
    };
    if (values.orden === '' || values.orden == null) {
      delete payload.orden;
    } else {
      const n = Number(values.orden);
      if (Number.isFinite(n)) payload.orden = n;
    }
    try {
      if (editingId) {
        const { data } = await api.put(`/v1/home-popup/${editingId}`, payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Actualizado', data.message);
      } else {
        const { data } = await api.post('/v1/home-popup', payload);
        if (!data.success) throw new Error(data.message);
        toastSuccess('Creado', data.message);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toastError('No se pudo guardar', e.message);
    }
  }

  async function onDelete(row) {
    const ok = await confirmDelete('¿Eliminar este aviso?', row.titulo);
    if (!ok) return;
    try {
      const { data } = await api.delete(`/v1/home-popup/${row.id}`);
      if (!data.success) throw new Error(data.message);
      toastSuccess('Eliminado', data.message);
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function toggleEstado(row) {
    try {
      const current = await api.get(`/v1/home-popup/${row.id}`);
      const r = current.data?.data;
      if (!r) throw new Error('No se pudo leer el registro');
      const { data } = await api.put(`/v1/home-popup/${row.id}`, {
        titulo: r.titulo,
        imagen: r.imagen,
        enlace: r.enlace ?? '',
        orden: r.orden,
        estado: Number(r.estado) === 1 ? 0 : 1,
      });
      if (!data.success) throw new Error(data.message);
      toastSuccess('Estado actualizado', '');
      await load();
    } catch (e) {
      toastError('Error', e.message);
    }
  }

  async function onPickImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const path = await uploadFile(file);
      setValue('imagen', path, { shouldValidate: true });
      toastSuccess('Imagen lista', '');
    } catch (err) {
      toastError('Subida fallida', err.message);
    } finally {
      e.target.value = '';
    }
  }

  const columns = useMemo(
    () => [
      {
        key: 'orden',
        label: 'Orden',
        render: (row) => (
          <span className="font-mono text-sm tabular-nums text-slate-600">{row.orden ?? '—'}</span>
        ),
      },
      { key: 'titulo', label: 'Título' },
      {
        key: 'estado',
        label: 'En inicio',
        render: (row) => (
          <button
            type="button"
            onClick={() => toggleEstado(row)}
            className={adminPublishStatusClasses(Number(row.estado) === 1)}
          >
            {Number(row.estado) === 1 ? 'Activo' : 'Inactivo'}
          </button>
        ),
      },
      {
        key: 'fecha_actualizacion',
        label: 'Actualizado',
        render: (row) => new Date(row.fecha_actualizacion).toLocaleString('es-CO'),
      },
      {
        key: 'actions',
        label: '',
        render: (row) => (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={ADMIN_ICON_BTN_CLASS}
              title="Vista previa"
              onClick={() => {
                setPreviewRow(row);
                setPreviewOpen(true);
              }}
            >
              <Eye className="h-4 w-4" />
            </button>
            <button type="button" className={ADMIN_ICON_BTN_CLASS} title="Editar" onClick={() => openEdit(row)}>
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" className={ADMIN_DELETE_BTN_CLASS} title="Eliminar" onClick={() => onDelete(row)}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-blue md:text-4xl">Avisos emergentes (inicio)</h1>
          <p className="mt-2 max-w-2xl text-slate-600">
            Flyers o imágenes de eventos importantes que aparecen como ventana emergente al entrar al home. Solo puede
            haber <strong className="text-slate-800">un aviso activo</strong> a la vez; al activar uno, los demás se
            desactivan. Los cambios quedan registrados en auditoría.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Nuevo aviso
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        page={page}
        total={total}
        perPage={perPage}
        onPageChange={setPage}
      />

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Editar aviso' : 'Nuevo aviso'}
        wide
      >
        <form className="grid gap-5 md:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
          <FormField label="Título (referencia interna)" error={errors.titulo?.message}>
            <input className={inputClass} {...register('titulo', { required: 'Requerido' })} />
          </FormField>
          <FormField label="Orden" hint="Menor número = prioridad si hubiera varios activos.">
            <input
              type="number"
              min={0}
              step={1}
              className={inputClass}
              {...register('orden')}
            />
          </FormField>
          <div className="md:col-span-2">
            <FormField label="Enlace opcional" hint="Al hacer clic en la imagen (URL interna o externa).">
              <input className={inputClass} placeholder="https://… o /noticias" {...register('enlace')} />
            </FormField>
          </div>
          <div className="md:col-span-2">
            <FormField
              label="Imagen del flyer"
              hint={uploading ? 'Subiendo…' : 'PNG, JPG o WebP. Obligatoria para publicar.'}
              error={errors.imagen?.message}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input type="file" accept="image/*" onChange={onPickImage} disabled={uploading} />
                <input
                  type="text"
                  className={`${inputClass} text-xs`}
                  placeholder="uploads/…"
                  {...register('imagen', { required: 'Sube o indica la ruta de la imagen' })}
                />
              </div>
              {imagenPath ? (
                <img
                  src={assetUrl(imagenPath)}
                  alt=""
                  className="mt-3 max-h-48 rounded-lg border border-slate-200 object-contain"
                />
              ) : null}
            </FormField>
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300 md:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-accent-lime-dark focus:ring-brand-green"
              {...register('estado')}
            />
            <span className="text-slate-700">Mostrar en la página de inicio</span>
          </label>
          <div className="flex flex-wrap gap-3 md:col-span-2">
            <Button type="submit">{editingId ? 'Guardar cambios' : 'Crear aviso'}</Button>
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      </Modal>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)} title="Vista previa del popup" wide>
        {previewRow && (
          <div className="mx-auto max-w-md space-y-4">
            <p className="text-sm font-semibold text-white">{previewRow.titulo}</p>
            {previewRow.imagen ? (
              <img
                src={assetUrl(previewRow.imagen)}
                alt=""
                className="w-full rounded-xl border border-white/10 object-contain"
              />
            ) : (
              <p className="text-sm text-slate-500">Sin imagen</p>
            )}
            {previewRow.enlace ? (
              <p className="text-xs text-slate-400">
                Enlace: <span className="text-slate-300">{previewRow.enlace}</span>
              </p>
            ) : null}
            <p className="text-xs text-slate-500">
              Estado: {Number(previewRow.estado) === 1 ? 'Activo en inicio' : 'Inactivo'}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
